import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';

// پرامپت‌های پیشنهادی
const SUGGESTED_PROMPTS = {
  registered: [
    {
      icon: '💡',
      title: 'تحلیل طرح من',
      text: 'طرح‌های من را تحلیل کن و نقاط قوت و ضعف آن‌ها را بگو'
    },
    {
      icon: '📊',
      title: 'تحلیل بازار',
      text: 'بازار هدف طرح من را تحلیل کن و درباره رقبا بگو'
    },
    {
      icon: '🗺️',
      title: 'نقشه راه توسعه',
      text: 'یک نقشه راه برای توسعه طرح من پیشنهاد بده'
    },
    {
      icon: '🚀',
      title: 'مراحل بعدی',
      text: 'برای پیشرفت طرح‌های من چه کارهایی باید انجام دهم؟'
    }
  ],
  guest: [
    {
      icon: '💭',
      title: 'ایده‌پردازی',
      text: 'می‌خوام یه ایده نوآورانه پیدا کنم. از کجا شروع کنم؟'
    },
    {
      icon: '🎯',
      title: 'جشنواره‌ها',
      text: 'درباره جشنواره‌های نوآفرین شو بهم بگو'
    },
    {
      icon: '📚',
      title: 'مراحل نوآوری',
      text: 'مراحل تبدیل ایده به محصول چیه؟'
    },
    {
      icon: '🎓',
      title: 'ثبت‌نام در سامانه',
      text: 'چطور می‌تونم در سامانه نوآفرین شو ثبت‌نام کنم؟'
    }
  ]
};

const ChatInterface = ({ userType, userId, isLoggedIn, onLimitExceeded, onUsageStatusChange }) => {
  const { isDarkMode } = useTheme();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatId, setChatId] = useState(null);
  const [chats, setChats] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    loadChats();
    checkUsageStatus();
    // در موبایل سایدبار پیش‌فرض بسته است
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  }, [userId]);

  const checkUsageStatus = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('userToken');
      
      let url, headers = { 'Content-Type': 'application/json' };
      
      if (userType === 'registered') {
        url = `${BACKEND_URL}/api/novayar/usage-status/user`;
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        url = `${BACKEND_URL}/api/novayar/usage-status/guest/${userId}`;
      }

      const response = await fetch(url, { headers });
      const data = await response.json();
      
      if (response.ok) {
        onUsageStatusChange(data);
      }
    } catch (error) {
      console.error('Error checking usage status:', error);
    }
  };

  const loadChats = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('userToken');
      
      let url, headers = {};
      
      if (userType === 'registered') {
        url = `${BACKEND_URL}/api/novayar/chats/user`;
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        url = `${BACKEND_URL}/api/novayar/chats/guest/${userId}`;
      }

      const response = await fetch(url, { headers });
      if (response.ok) {
        const data = await response.json();
        setChats(data);
      }
    } catch (error) {
      console.error('Error loading chats:', error);
    }
  };

  const loadChat = async (selectedChatId) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('userToken');
      
      let url, headers = {};
      
      if (userType === 'registered') {
        url = `${BACKEND_URL}/api/novayar/chat/${selectedChatId}/user`;
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        url = `${BACKEND_URL}/api/novayar/chat/${selectedChatId}/guest/${userId}`;
      }

      const response = await fetch(url, { headers });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
        setChatId(selectedChatId);
        setShowSuggestions(false);
        // در موبایل سایدبار را ببند
        if (window.innerWidth < 1024) {
          setSidebarOpen(false);
        }
      }
    } catch (error) {
      console.error('Error loading chat:', error);
    }
  };

  const sendMessage = async (e, customMessage = null) => {
    if (e) e.preventDefault();
    const messageToSend = customMessage || inputMessage.trim();
    if (!messageToSend || loading) return;

    const userMessage = messageToSend;
    setInputMessage('');
    setLoading(true);
    setShowSuggestions(false);

    // افزودن پیام کاربر به UI
    const tempUserMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, tempUserMessage]);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('userToken');
      
      let url, headers = { 'Content-Type': 'application/json' };
      
      if (userType === 'registered') {
        url = `${BACKEND_URL}/api/novayar/chat/user`;
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        url = `${BACKEND_URL}/api/novayar/chat/guest/${userId}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message: userMessage,
          chat_id: chatId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'خطا در ارسال پیام');
      }

      if (data.error === 'limit_exceeded') {
        // محدودیت تمام شده
        const errorMessage = {
          role: 'system',
          content: data.message,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
        onLimitExceeded();
      } else if (data.success) {
        // پیام موفق
        const assistantMessage = {
          role: 'assistant',
          content: data.response,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
        
        // به‌روزرسانی chat_id و usage status
        if (data.chat_id && !chatId) {
          setChatId(data.chat_id);
          loadChats();
        }
        
        onUsageStatusChange({
          can_use: data.remaining > 0 || data.has_subscription,
          remaining: data.remaining,
          has_subscription: data.has_subscription
        });
      }
    } catch (error) {
      const errorMessage = {
        role: 'system',
        content: `خطا: ${error.message}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      // فوکوس به input
      inputRef.current?.focus();
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setChatId(null);
    setShowSuggestions(true);
    inputRef.current?.focus();
  };

  // Format text with basic markdown support
  const formatMessage = (text) => {
    // تبدیل لینک‌ها
    let formatted = text.replace(
      /(https?:\/\/[^\s]+)/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-400 hover:text-blue-300 underline">$1</a>'
    );
    
    // Bold
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold">$1</strong>');
    
    // Italic
    formatted = formatted.replace(/\*(.*?)\*/g, '<em class="italic">$1</em>');
    
    // Line breaks
    formatted = formatted.replace(/\n/g, '<br />');
    
    return formatted;
  };

  return (
    <div className="flex h-[calc(100vh-200px)] min-h-[600px] max-w-[1800px] mx-auto">
      {/* Backdrop برای موبایل */}
      <AnimatePresence>
        {sidebarOpen && window.innerWidth < 1024 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Sidebar - Recent Chats */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ x: -300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className={`fixed lg:relative z-50 h-full w-[280px] lg:w-[320px] flex flex-col transition-colors duration-300 ${
              isDarkMode 
                ? 'bg-gradient-to-b from-gray-900 to-gray-950 border-r border-gray-800' 
                : 'bg-gradient-to-b from-gray-50 to-white border-r border-gray-200'
            }`}
          >
            {/* Sidebar Header */}
            <div className="p-4 border-b border-gray-700/50">
              <button
                onClick={startNewChat}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-3 px-4 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
                data-testid="new-chat-button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                <span>چت جدید</span>
              </button>
            </div>

            {/* Chats List */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar">
              {chats.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-4xl mb-3">💬</div>
                  <p className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    هنوز چتی ندارید
                  </p>
                </div>
              ) : (
                chats.map((chat) => (
                  <motion.button
                    key={chat.id}
                    onClick={() => loadChat(chat.id)}
                    whileHover={{ scale: 1.02, x: 4 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full text-right p-3 rounded-xl transition-all duration-200 border ${
                      chatId === chat.id
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg border-transparent'
                        : isDarkMode
                        ? 'bg-gray-800/50 hover:bg-gray-800 text-gray-200 border-gray-700/50 hover:border-blue-500/50'
                        : 'bg-white hover:bg-gray-50 text-gray-800 border-gray-200 hover:border-blue-300'
                    }`}
                    data-testid={`chat-item-${chat.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-xl flex-shrink-0">💬</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {chat.messages?.[0]?.content?.substring(0, 40) || 'چت جدید'}...
                        </p>
                        <p className={`text-xs mt-1 ${
                          chatId === chat.id ? 'text-blue-200' : isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {new Date(chat.updated_at).toLocaleDateString('fa-IR', { month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  </motion.button>
                ))
              )}
            </div>

            {/* Sidebar Footer - User Info */}
            {isLoggedIn && (
              <div className={`p-4 border-t transition-colors duration-300 ${
                isDarkMode ? 'border-gray-700/50 bg-gray-900/50' : 'border-gray-200 bg-gray-50'
              }`}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                    👤
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-medium truncate transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      کاربر ثبت‌نام شده
                    </p>
                    <p className="text-xs text-green-500 flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                      آنلاین
                    </p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Chat Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b transition-colors duration-300 ${
          isDarkMode 
            ? 'bg-gray-900/50 border-gray-800 backdrop-blur-sm' 
            : 'bg-white/50 border-gray-200 backdrop-blur-sm'
        }`}>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`p-2 rounded-lg transition-all duration-200 ${
                isDarkMode 
                  ? 'hover:bg-gray-800 text-gray-300' 
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
              data-testid="toggle-sidebar-button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="flex items-center gap-3">
              <motion.div 
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 5 }}
                className="text-3xl"
              >
                🤖
              </motion.div>
              <div>
                <h3 className={`font-bold text-lg transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-800'
                }`}>
                  نوآیار
                </h3>
                <p className="text-xs text-green-500 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                  آنلاین
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div 
          className={`flex-1 overflow-y-auto px-4 py-6 space-y-6 custom-scrollbar transition-colors duration-300 ${
            isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
          }`}
          data-testid="chat-messages-container"
        >
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-2xl">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200 }}
                  className="text-7xl mb-6"
                >
                  👋
                </motion.div>
                <motion.h4 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className={`text-3xl font-bold mb-3 transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-800'
                  }`}
                >
                  سلام! من نوآیار هستم
                </motion.h4>
                <motion.p 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className={`text-lg mb-8 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}
                >
                  {isLoggedIn
                    ? 'آماده‌ام تا درباره طرح‌های شما صحبت کنم و به شما در مسیر نوآوری کمک کنم 🚀'
                    : 'آماده‌ام تا به شما در یافتن ایده، آشنایی با مراحل نوآوری و اطلاعات جشنواره‌ها کمک کنم 🌟'
                  }
                </motion.p>

                {/* Suggested Prompts */}
                {showSuggestions && (
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl mx-auto"
                  >
                    {(isLoggedIn ? SUGGESTED_PROMPTS.registered : SUGGESTED_PROMPTS.guest).map((prompt, index) => (
                      <motion.button
                        key={index}
                        onClick={() => sendMessage(null, prompt.text)}
                        whileHover={{ scale: 1.03, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        className={`p-4 rounded-2xl border-2 transition-all duration-300 text-right group ${
                          isDarkMode
                            ? 'bg-gray-800 border-gray-700 hover:border-blue-500 hover:bg-gray-750'
                            : 'bg-white border-gray-200 hover:border-blue-400 hover:shadow-lg'
                        }`}
                        data-testid={`suggestion-${index}`}
                      >
                        <div className="flex items-start gap-3">
                          <span className="text-3xl flex-shrink-0">{prompt.icon}</span>
                          <div className="flex-1 min-w-0">
                            <h5 className={`font-bold text-sm mb-1 transition-colors duration-300 ${
                              isDarkMode ? 'text-white group-hover:text-blue-400' : 'text-gray-800 group-hover:text-blue-600'
                            }`}>
                              {prompt.title}
                            </h5>
                            <p className={`text-xs line-clamp-2 transition-colors duration-300 ${
                              isDarkMode ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                              {prompt.text}
                            </p>
                          </div>
                        </div>
                      </motion.button>
                    ))}
                  </motion.div>
                )}
              </div>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto space-y-6">
              {messages.map((msg, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex items-start gap-4 ${
                    msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                  }`}
                  data-testid={`message-${msg.role}-${index}`}
                >
                  {/* Avatar */}
                  <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-xl ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-br from-blue-500 to-purple-500'
                      : msg.role === 'system'
                      ? 'bg-gradient-to-br from-red-500 to-orange-500'
                      : 'bg-gradient-to-br from-green-500 to-teal-500'
                  }`}>
                    {msg.role === 'user' ? '👤' : msg.role === 'system' ? '⚠️' : '🤖'}
                  </div>

                  {/* Message */}
                  <div className={`flex-1 max-w-[80%] ${
                    msg.role === 'user' ? 'text-right' : 'text-right'
                  }`}>
                    <div className={`inline-block px-5 py-3 rounded-2xl transition-colors duration-300 ${
                      msg.role === 'user'
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                        : msg.role === 'system'
                        ? isDarkMode
                          ? 'bg-red-900/40 text-red-300 border border-red-700'
                          : 'bg-red-100 text-red-800 border border-red-300'
                        : isDarkMode
                        ? 'bg-gray-800 text-white border border-gray-700 shadow-lg'
                        : 'bg-white text-gray-800 border border-gray-200 shadow-md'
                    }`}>
                      <div 
                        className="text-base leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                      />
                      <p className={`text-xs mt-2 opacity-70`}>
                        {new Date(msg.timestamp).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
              
              {/* Typing Indicator */}
              {loading && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-start gap-4"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center text-xl">
                    🤖
                  </div>
                  <div className={`px-5 py-3 rounded-2xl transition-colors duration-300 ${
                    isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-md'
                  }`}>
                    <div className="flex gap-2">
                      <motion.div
                        animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 1, repeat: Infinity, delay: 0 }}
                        className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                      />
                      <motion.div
                        animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                        className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                      />
                      <motion.div
                        animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                        className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input */}
        <div className={`p-4 border-t transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'
        }`}>
          <form onSubmit={sendMessage} className="max-w-4xl mx-auto">
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <textarea
                  ref={inputRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage(e);
                    }
                  }}
                  placeholder="پیام خود را بنویسید... (Enter برای ارسال، Shift+Enter برای خط جدید)"
                  disabled={loading}
                  rows={1}
                  className={`w-full px-4 py-3 rounded-2xl border-2 text-base transition-all duration-300 resize-none focus:outline-none ${
                    isDarkMode
                      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:border-blue-500 focus:bg-gray-750'
                      : 'bg-gray-50 border-gray-300 text-gray-800 placeholder-gray-500 focus:border-blue-500 focus:bg-white'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  style={{ minHeight: '52px', maxHeight: '150px' }}
                  data-testid="chat-input"
                />
              </div>
              <motion.button
                type="submit"
                disabled={loading || !inputMessage.trim()}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold p-4 rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                style={{ minWidth: '52px', height: '52px' }}
                data-testid="send-message-button"
              >
                {loading ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </motion.div>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                )}
              </motion.button>
            </div>
            <p className={`text-xs mt-2 text-center transition-colors duration-300 ${
              isDarkMode ? 'text-gray-500' : 'text-gray-400'
            }`}>
              نوآیار ممکن است اشتباه کند. لطفاً اطلاعات مهم را بررسی کنید.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;